import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { PageTitle } from "../components/PageTitle";
import AccountSidebar from "../components/AccountSidebar";
import {
  getOrderDetail,
  requestCancellation,
  downloadInvoice,
} from "../api/checkout";
import { createReview } from "../api/reviews";
import { getImageUrl } from "../api/utils";
import { showErrorMessage, showSuccessMessage, sizesLabel, OK } from "../utils";
import MyAccountSidebarMobile from "../components/MyAccountSidebarMobile";

const FALLBACK_IMAGE = "/assets/images/product-placeholder.svg";

// ── Review Modal Component ────────────────────────────────
const ReviewModal = ({ item, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      showErrorMessage("Please write a review comment");
      return;
    }
    try {
      setSubmitting(true);
      const res = await createReview(item.product_slug, {
        rating,
        title,
        comment,
      });
      if (res?.status !== OK) throw res?.error || "Failed to submit review";
      showSuccessMessage("Review submitted successfully! 🌈");
      onSubmitted(item.product_id);
      onClose();
    } catch (err) {
      showErrorMessage(
        typeof err === "string" ? err : "Failed to submit review",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 9998,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
      >
        <div
          style={{
            backgroundColor: "#fff",
            width: "100%",
            maxWidth: 480,
            padding: 32,
            borderRadius: 8,
            position: "relative",
          }}
        >
          {/* Header */}
          <h5 style={{ color: "#2c3a34", marginBottom: 4 }}>Write a Review</h5>
          <p style={{ color: "#888", fontSize: 13, marginBottom: 20 }}>
            {item.product_name}
          </p>

          <form onSubmit={handleSubmit}>
            {/* Star Rating */}
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#2c3a34",
                  display: "block",
                  marginBottom: 8,
                }}
              >
                Your Rating <span style={{ color: "red" }}>*</span>
              </label>
              <div style={{ display: "flex", gap: 4 }}>
                {[1, 2, 3, 4, 5].map((s) => (
                  <span
                    key={s}
                    onClick={() => setRating(s)}
                    style={{
                      fontSize: 32,
                      cursor: "pointer",
                      color: s <= rating ? "#f5a623" : "#ddd",
                      transition: "color 0.1s",
                    }}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>

            {/* Title */}
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#2c3a34",
                  display: "block",
                  marginBottom: 8,
                }}
              >
                Review Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your review a title"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  fontSize: 14,
                  color: "#333",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Comment */}
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#2c3a34",
                  display: "block",
                  marginBottom: 8,
                }}
              >
                Your Review <span style={{ color: "red" }}>*</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                placeholder="Share your experience with this product..."
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  fontSize: 14,
                  color: "#333",
                  resize: "vertical",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "1px solid #ddd",
                  color: "#555",
                  padding: "11px 0",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  flex: 1,
                  background: submitting ? "#888" : "#2c3a34",
                  border: "none",
                  color: "#fff",
                  padding: "11px 0",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                {submitting ? "Submitting..." : "Submit Review"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// ── Main OrderDetails Component ───────────────────────────
const OrderDetails = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  // ── Review Modal state ────────────────────────────────
  const [reviewItem, setReviewItem] = useState(null);
  const [reviewedProductIds, setReviewedProductIds] = useState([]);

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = async () => {
    setLoading(true);
    try {
      const res = await getOrderDetail(id);
      const orderData = res?.order || res;
      setOrder(orderData);

      // ── Pre-fill already reviewed product ids ────────
      const reviewed = (orderData?.reviews || []).map((r) => r.product_id);
      setReviewedProductIds(reviewed);
    } catch (err) {
      showErrorMessage(typeof err === "string" ? err : "Failed to load order");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!cancelReason.trim()) {
      showErrorMessage("Please provide a reason for cancellation");
      return;
    }
    setCancelling(true);
    try {
      await requestCancellation(order.id, cancelReason.trim());
      showSuccessMessage("Cancellation request submitted!");
      setShowCancelModal(false);
      setCancelReason("");
      await loadOrder();
    } catch (err) {
      showErrorMessage(
        typeof err === "string" ? err : "Could not submit request",
      );
    } finally {
      setCancelling(false);
    }
  };

  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      await downloadInvoice(order.id);
    } catch (err) {
      showErrorMessage("Could not download invoice");
    } finally {
      setDownloadingInvoice(false);
    }
  };

  // ── Review submitted callback ─────────────────────────
  const handleReviewSubmitted = (productId) => {
    setReviewedProductIds((prev) => [...prev, productId]);
  };

  const firstItem = order?.order_items?.[0];
  const subtotal =
    order?.order_items?.reduce(
      (sum, item) => sum + item.selling_price * item.quantity,
      0,
    ) || 0;
  const discountAmount = order?.discount?.discount_amount || 0;
  const formattedDate = order?.created_at
    ? new Date(order.created_at).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

  const formattedSyncDate = order?.fulfillment_order?.last_synced_at
    ? new Date(order?.fulfillment_order?.last_synced_at).toLocaleDateString(
        "en-IN",
        { day: "2-digit", month: "2-digit", year: "numeric" },
      )
    : "—";

  const shippingAddress = order?.address
    ? `${order.address.address_line1}${order.address.address_line2 ? ", " + order.address.address_line2 : ""}, ${order.address.city}, ${order.address.state} — ${order.address.pincode}`
    : "—";

  const tabs = ["Item Details", "Delivery Details", "Recipient Details"];

  return (
    <>
      <PageTitle
        title="My Account"
        subtitle="Your Hue. Your World. Your Space!"
        bgImage="default.png"
      />

      <section className="flat-spacing-11">
        <div className="container">
          <div className="row">
            <div className="col-lg-3">
              <AccountSidebar />
            </div>

            <div className="col-lg-9">
              <div className="mb_20">
                <Link to="/my-account/orders" className="tf-btn btn-line fw-6">
                  ← Back to Orders
                </Link>
              </div>

              {loading && (
                <div>
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        background: "#f4f3ef",
                        height: i === 0 ? 80 : 40,
                        marginBottom: 16,
                        animation: "pulse 1.4s ease-in-out infinite",
                      }}
                    />
                  ))}
                </div>
              )}

              {!loading && !order && (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <p style={{ color: "#888", marginBottom: 16 }}>
                    Order not found.
                  </p>
                  <Link
                    to="/my-account/orders"
                    className="tf-btn btn-fill animate-hover-btn"
                  >
                    Back to Orders
                  </Link>
                </div>
              )}

              {!loading && order && (
                <div className="wd-form-order">
                  {/* Order Head */}
                  <div className="order-head">
                    <figure className="img-product">
                      <img
                        src={
                          firstItem?.image_url
                            ? getImageUrl(firstItem.image_url) || FALLBACK_IMAGE
                            : FALLBACK_IMAGE
                        }
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = FALLBACK_IMAGE;
                        }}
                        alt={firstItem?.product_name || "product"}
                      />
                    </figure>
                    <div className="content">
                      <div className="badge">
                        {order.status?.replace(/_/g, " ")}
                      </div>
                      <h6 className="mt-8 fw-5">Order #{order.order_number}</h6>
                    </div>
                  </div>

                  {/* Meta Grid */}
                  <div className="tf-grid-layout md-col-2 gap-15">
                    <div className="item">
                      <div className="text-2 text_black-2">Total</div>
                      <div className="text-2 mt_4 fw-6">
                        ₹{Number(order.total_amount).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <div className="item">
                      <div className="text-2 text_black-2">Payment Method</div>
                      <div className="text-2 mt_4 fw-6">
                        {order.payment_method.toUpperCase()}
                      </div>
                    </div>
                    <div className="item">
                      <div className="text-2 text_black-2">Status</div>
                      <div className="text-2 mt_4 fw-6">
                        {order.status.toUpperCase()}
                      </div>
                    </div>
                    <div className="item">
                      <div className="text-2 text_black-2">Payment Status</div>
                      <div className="text-2 mt_4 fw-6">
                        {order.payment_method.toUpperCase() === "PREPAID"
                          ? order.payment_status === "unpaid"
                            ? "FAILED"
                            : "PAID"
                          : order.payment_method.toUpperCase() === "COD"
                            ? "PENDING"
                            : order.payment_status}
                      </div>
                    </div>
                    <div className="item">
                      <div className="text-2 text_black-2">Items</div>
                      <div className="text-2 mt_4 fw-6">
                        {order.order_items?.length || 0} item(s)
                      </div>
                    </div>
                    <div className="item">
                      <div className="text-2 text_black-2">Courier</div>
                      <div className="text-2 mt_4 fw-6">
                        {order.payment_status === "paid"
                          ? order.courier || "To be assigned"
                          : "N/A"}
                      </div>
                    </div>
                    <div className="item">
                      <div className="text-2 text_black-2">Order Date</div>
                      <div className="text-2 mt_4 fw-6">{formattedDate}</div>
                    </div>
                    <div className="item">
                      <div className="text-2 text_black-2">Address</div>
                      <div className="text-2 mt_4 fw-6">{shippingAddress}</div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="widget-tabs style-has-border widget-order-tab">
                    <ul className="widget-menu-tab">
                      {tabs.map((tab, i) => (
                        <li
                          key={i}
                          className={`item-title${activeTab === i ? " active" : ""}`}
                          onClick={() => setActiveTab(i)}
                          style={{ cursor: "pointer" }}
                        >
                          <span className="inner">{tab}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="widget-content-tab">
                      {/* ── Tab 1: Item Details ── */}
                      <div
                        className={`widget-content-inner${activeTab === 0 ? " active" : ""}`}
                      >
                        {order.order_items?.map((item) => {
                          const alreadyReviewed = reviewedProductIds.includes(
                            item.product_id,
                          );
                          const canReview = order.status === "delivered";

                          return (
                            <div
                              key={item.id}
                              style={{
                                borderBottom: "1px solid #f0f0f0",
                                paddingBottom: 16,
                                marginBottom: 16,
                              }}
                            >
                              <div className="order-head">
                                <figure className="img-product">
                                  <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href={`/products/${item.product_slug}`}
                                  >
                                    <img
                                      src={
                                        item.image_url
                                          ? getImageUrl(item.image_url) ||
                                            FALLBACK_IMAGE
                                          : FALLBACK_IMAGE
                                      }
                                      onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = FALLBACK_IMAGE;
                                      }}
                                      alt={item.product_name}
                                    />
                                  </a>
                                </figure>
                                <div className="content">
                                  <div className="text-2 fw-6">
                                    <a
                                      target="_blank"
                                      rel="noreferrer"
                                      href={`/products/${item.product_slug}`}
                                    >
                                      {item.product_name}
                                    </a>
                                  </div>
                                  <div className="mt_4">
                                    <span className="fw-6">Price : </span>₹
                                    {Number(item.selling_price).toLocaleString(
                                      "en-IN",
                                    )}
                                  </div>
                                  {item.size && (
                                    <div className="mt_4">
                                      <span className="fw-6">Size : </span>
                                      {sizesLabel[item.size] || item.size}
                                    </div>
                                  )}
                                  {item.color && (
                                    <div className="mt_4">
                                      <span className="fw-6">Color : </span>
                                      {item.color}
                                    </div>
                                  )}
                                  <div className="mt_4">
                                    <span className="fw-6">Qty : </span>
                                    {item.quantity}
                                  </div>
                                </div>
                              </div>

                              {/* ── Review Button ── */}
                              {canReview && (
                                <div style={{ marginTop: 12 }}>
                                  {alreadyReviewed ? (
                                    <span
                                      style={{
                                        fontSize: 13,
                                        color: "#2e7d32",
                                        fontWeight: 600,
                                      }}
                                    >
                                      ✓ Review Submitted
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => setReviewItem(item)}
                                      style={{
                                        background: "transparent",
                                        border: "1px solid #2c3a34",
                                        color: "#2c3a34",
                                        padding: "7px 16px",
                                        fontSize: 13,
                                        fontWeight: 600,
                                        cursor: "pointer",
                                      }}
                                    >
                                      ★ Write a Review
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Price Summary */}
                        <ul className="mt_20">
                          <li className="d-flex justify-content-between text-2">
                            <span>Subtotal</span>
                            <span className="fw-6">
                              ₹{subtotal.toLocaleString("en-IN")}
                            </span>
                          </li>
                          {discountAmount > 0 && (
                            <li className="d-flex justify-content-between text-2 mt_4">
                              <span>
                                Discount ({order.discount?.coupon_code})
                              </span>
                              <span
                                className="fw-6"
                                style={{ color: "#2e7d32" }}
                              >
                                - ₹
                                {Number(discountAmount).toLocaleString("en-IN")}
                              </span>
                            </li>
                          )}
                          {order.payment_method === "COD" && (
                            <li className="d-flex justify-content-between text-2 mt_4">
                              <span>COD Charges</span>
                              <span
                                className="fw-6"
                                style={{
                                  color:
                                    order.cod_charge === 0
                                      ? "#2e7d32"
                                      : undefined,
                                }}
                              >
                                {order.cod_charge === 0
                                  ? "FREE"
                                  : `₹${Number(order.cod_charge).toLocaleString("en-IN")}`}
                              </span>
                            </li>
                          )}
                          <li className="d-flex justify-content-between text-2 mt_4">
                            <span>Shipping</span>
                            <span
                              className="fw-6"
                              style={{
                                color:
                                  order.shipping_cost === 0
                                    ? "#2e7d32"
                                    : undefined,
                              }}
                            >
                              {order.shipping_cost === 0
                                ? "FREE"
                                : `₹${Number(order.shipping_cost).toLocaleString("en-IN")}`}
                            </span>
                          </li>
                          <li className="d-flex justify-content-between text-2 mt_4 pb_8 line">
                            <span>Order Total</span>
                            <span className="fw-6">
                              ₹
                              {Number(order.total_amount).toLocaleString(
                                "en-IN",
                              )}
                            </span>
                          </li>
                        </ul>

                        {/* Actions */}
                        <div className="d-flex gap-10 mt_20 flex-wrap">
                          {(order.status === "pending" ||
                            order.status === "confirmed") &&
                            !order.cancellation_request && (
                              <button
                                onClick={() => setShowCancelModal(true)}
                                style={{
                                  background: "transparent",
                                  border: "1px solid #c62828",
                                  color: "#c62828",
                                  padding: "8px 18px",
                                  cursor: "pointer",
                                  fontSize: 13,
                                  fontWeight: 600,
                                }}
                              >
                                Cancel Order
                              </button>
                            )}
                          {order.cancellation_request?.status === "pending" && (
                            <span
                              style={{
                                fontSize: 13,
                                color: "#f59e0b",
                                fontWeight: 600,
                                padding: "8px 0",
                              }}
                            >
                              ⏳ Cancellation Pending
                            </span>
                          )}
                          {order.payment_status === "paid" && (
                            <button
                              onClick={handleDownloadInvoice}
                              disabled={downloadingInvoice}
                              style={{
                                background: "transparent",
                                border: "1px solid #2c3a34",
                                color: "#2c3a34",
                                padding: "8px 18px",
                                cursor: downloadingInvoice
                                  ? "not-allowed"
                                  : "pointer",
                                fontSize: 13,
                                fontWeight: 600,
                                opacity: downloadingInvoice ? 0.7 : 1,
                              }}
                            >
                              {downloadingInvoice
                                ? "Downloading..."
                                : "↓ Download Invoice"}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* ── Tab 2: Delivery Details ── */}
                      <div
                        className={`widget-content-inner${activeTab === 1 ? " active" : ""}`}
                      >
                        {order.fulfillment_order?.status ? (
                          <ul>
                            <li className="text-2 mb_8">
                              <strong>Fulfillment Status: </strong>
                              {order.fulfillment_order?.status
                                .toUpperCase()
                                .replace("_", " ")}
                            </li>
                            <li className="text-2 mb_8">
                              <strong>Courier : </strong>
                              {order.fulfillment_order?.courier || "—"}
                            </li>
                            <li className="text-2 mb_8">
                              <strong>Tracking Number : </strong>
                              {order.fulfillment_order?.tracking_number || "—"}
                            </li>
                            {order.fulfillment_order?.tracking_number && (
                              <li className="text-2 mb_8">
                                <strong>Tracking Link : </strong>
                                <a
                                  href={
                                    order.fulfillment_order?.tracking_link ||
                                    "#"
                                  }
                                >
                                  {order.fulfillment_order?.tracking_link ||
                                    "—"}
                                </a>
                              </li>
                            )}
                            <li className="text-2 mb_8">
                              <strong>Last Synced At : </strong>
                              {formattedSyncDate}
                            </li>
                          </ul>
                        ) : (
                          <p style={{ color: "#888", fontSize: 14 }}>
                            Courier details will be updated once your order is
                            shipped. 🚚
                          </p>
                        )}
                      </div>

                      {/* ── Tab 3: Recipient Details ── */}
                      <div
                        className={`widget-content-inner${activeTab === 2 ? " active" : ""}`}
                      >
                        <p className="text-2 text_success">
                          Thank you! Your order has been received.
                        </p>
                        <ul className="mt_20">
                          <li className="text-2 mb_8">
                            <strong>Order Number : </strong>#
                            {order.order_number}
                          </li>
                          <br />
                          <li className="text-2 mb_8">
                            <strong>Recipient Name : </strong>
                            {order.address?.name}
                          </li>
                          <li className="text-2 mb_8">
                            <strong>Address Line 1 : </strong>
                            {order.address?.address_line1}
                          </li>
                          <li className="text-2 mb_8">
                            <strong>Address Line 2 : </strong>
                            {order.address?.address_line2}
                          </li>
                          <li className="text-2 mb_8">
                            <strong>City : </strong>
                            {order.address?.city}
                          </li>
                          <li className="text-2 mb_8">
                            <strong>State : </strong>
                            {order.address?.state}
                          </li>
                          <li className="text-2 mb_8">
                            <strong>Pincode : </strong>
                            {order.address?.pincode}
                          </li>
                          <li className="text-2 mb_8">
                            <strong>Phone : </strong>
                            {order.address?.phone}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile sidebar */}
      <div className="btn-sidebar-account">
        <button onClick={() => setSidebarOpen(true)}>
          <i className="icon icon-sidebar-2" />
        </button>
      </div>
      <MyAccountSidebarMobile
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* ── Review Modal ── */}
      {reviewItem && (
        <ReviewModal
          item={reviewItem}
          onClose={() => setReviewItem(null)}
          onSubmitted={handleReviewSubmitted}
        />
      )}

      {/* ── Cancel Modal ── */}
      {showCancelModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              width: "100%",
              maxWidth: 460,
              padding: 32,
              position: "relative",
            }}
          >
            <button
              onClick={() => setShowCancelModal(false)}
              style={{
                position: "absolute",
                top: 16,
                right: 20,
                background: "none",
                border: "none",
                fontSize: 24,
                cursor: "pointer",
                color: "#888",
              }}
            >
              ×
            </button>
            <h5 style={{ color: "#2c3a34", marginBottom: 4 }}>Cancel Order</h5>
            <p style={{ color: "#888", fontSize: 13, marginBottom: 20 }}>
              #{order?.order_number}
            </p>
            <label style={{ fontSize: 13, color: "#2c3a34", fontWeight: 600 }}>
              Reason <span style={{ color: "red" }}>*</span>
            </label>
            <textarea
              rows={4}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Please tell us why you want to cancel..."
              style={{
                width: "100%",
                marginTop: 8,
                padding: "10px 12px",
                fontSize: 13,
                border: "1px solid #ddd",
                resize: "vertical",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button
                onClick={() => setShowCancelModal(false)}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "1px solid #ddd",
                  color: "#555",
                  padding: "11px 0",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Go Back
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={cancelling}
                style={{
                  flex: 1,
                  background: cancelling ? "#888" : "#c62828",
                  border: "none",
                  color: "#fff",
                  padding: "11px 0",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: cancelling ? "not-allowed" : "pointer",
                }}
              >
                {cancelling ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderDetails;
